// src/pages/MenuManagement.jsx

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputAdornment,
  Tooltip,
  CircularProgress,
  Alert,
  Snackbar,
  Tabs,
  Tab,
  Badge,
  Stepper,
  Step,
  StepLabel,
  CardActionArea,
  useTheme,
  useMediaQuery,
  Fade,
  Stack,
  alpha,
  Skeleton,
  Container,
  Zoom,
  Grow,
  Fab,
  Drawer
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Image as ImageIcon,
  Fastfood as FastFoodIcon,
  LocalPizza as PizzaIcon,
  LocalFireDepartment as BbqIcon,
  LocalBar as DrinksIcon,
  RiceBowl as DesiIcon,
  AttachMoney as MoneyIcon,
  Timer as TimerIcon,
  Kitchen as KitchenIcon,
  Save as SaveIcon,
  CloudUpload as UploadIcon,
  Restaurant as RestaurantIcon,
  Category as CategoryIcon,
  AddCircle as AddCircleIcon,
  RemoveCircle as RemoveIcon,
  LocalOffer as DealIcon,
  ShoppingBasket as BasketIcon,
  Refresh as RefreshIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  GridView as GridViewIcon,
  ViewList as ListViewIcon,
  Menu as MenuIcon
} from '@mui/icons-material';

// ✅ FIXED: Direct import from storage.js
import db from '../database/storage';

// ==================== CONSTANTS ====================
const CATEGORY_ICONS = {
  fast_food: <FastFoodIcon />,
  desi: <DesiIcon />,
  bbq: <BbqIcon />,
  drinks: <DrinksIcon />,
  pizza: <PizzaIcon />,
  default: <RestaurantIcon />,
};

const CATEGORY_COLORS = {
  fast_food: '#ff5722',
  desi: '#4caf50',
  bbq: '#795548',
  drinks: '#2196f3',
  pizza: '#ff9800',
  default: '#757575',
};

const KITCHEN_STATIONS = ['main', 'grill', 'tandoor', 'bar', 'fryer', 'dessert'];

const SIZE_TEMPLATES = {
  pizza: [
    { size: 'Small', price: '' },
    { size: 'Regular', price: '' },
    { size: 'Large', price: '' },
    { size: 'XL', price: '' },
    { size: 'Jumbo', price: '' },
  ],
  burger: [
    { size: 'Regular', price: '' },
    { size: 'Large', price: '' },
    { size: 'Double', price: '' },
  ],
  fast_food: [
    { size: 'Regular', price: '' },
    { size: 'Large', price: '' },
    { size: 'Family', price: '' },
  ],
  bbq: [
    { size: 'Half', price: '' },
    { size: 'Full', price: '' },
  ],
  desi: [
    { size: 'Half', price: '' },
    { size: 'Full', price: '' },
    { size: 'Double', price: '' },
  ],
  karahi: [
    { size: 'Half', price: '' },
    { size: 'Full', price: '' },
    { size: 'Double', price: '' },
  ],
  drinks: [
    { size: 'Small', price: '' },
    { size: 'Regular', price: '' },
    { size: 'Large', price: '' },
    { size: 'Jumbo', price: '' },
  ],
  default: [
    { size: 'Regular', price: '' },
    { size: 'Large', price: '' },
  ],
};

// ==================== DEAL BUILDER STEPS (FIXED) ====================
const dealBuilderSteps = ['Select Category', 'Select Item', 'Choose Variant & Qty'];

// ==================== HELPERS ====================
const parseVariants = (variantsData) => {
  if (!variantsData) return [];
  if (Array.isArray(variantsData)) return variantsData;
  if (typeof variantsData === 'string') {
    try {
      const parsed = JSON.parse(variantsData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const parseDealItems = (dealItemsData) => {
  if (!dealItemsData) return [];
  if (Array.isArray(dealItemsData)) return dealItemsData;
  if (typeof dealItemsData === 'string') {
    try {
      const parsed = JSON.parse(dealItemsData);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }
  return [];
};

const getItemDisplayPrice = (item) => {
  if (!item) return 0;
  const variants = parseVariants(item.variants);
  if (variants.length > 0) {
    const prices = variants.map(v => parseFloat(v.price) || 0).filter(p => p > 0);
    return prices.length > 0 ? Math.min(...prices) : 0;
  }
  return parseFloat(item.price) || 0;
};

// ==================== API WRAPPER (FIXED - Uses db directly) ====================
const getAPI = () => {
  // ✅ ALWAYS use db from storage.js
  if (db) {
    console.log('[MenuManagement] ✅ Using db (storage.js)');
    return db;
  }
  
  // Fallback for electron
  if (typeof window !== 'undefined' && window.electronAPI) {
    console.log('[MenuManagement] ✅ Using electronAPI');
    return window.electronAPI;
  }
  
  console.error('[MenuManagement] ❌ No API available');
  return null;
};

const formatPrice = (price) => `PKR ${Number(price).toLocaleString('en-IN')}`;

// ==================== MAIN COMPONENT ====================
const MenuManagement = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // ===== STATE =====
  const [categories, setCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [deals, setDeals] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [activeTab, setActiveTab] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [mobileDrawer, setMobileDrawer] = useState(false);

  // ===== IMAGE URLS STATE =====
  const [imageUrls, setImageUrls] = useState({});

  // ===== CATEGORY DIALOG =====
  const [categoryDialog, setCategoryDialog] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: '', name_urdu: '', type: 'fast_food', color: '#ff5722' });

  // ===== ITEM DIALOG =====
  const [itemDialog, setItemDialog] = useState(false);
  const [itemDialogMode, setItemDialogMode] = useState('add');
  const [editingItem, setEditingItem] = useState(null);
  const [itemForm, setItemForm] = useState({
    category_id: '',
    name: '',
    name_urdu: '',
    description: '',
    price: '',
    cost_price: '',
    kitchen_station: 'main',
    prep_time: 15,
    is_veg: false,
    is_active: true,
    image: null,
    has_variants: false,
    variants: [],
  });
  const [previewImage, setPreviewImage] = useState(null);
  const fileInputRef = useRef(null);

  // ===== MODIFIERS =====
  const [modifiers, setModifiers] = useState([]);
  const [newModifier, setNewModifier] = useState({ name: '', price: '' });

  // ===== DEAL DIALOG =====
  const [dealDialog, setDealDialog] = useState(false);
  const [dealDialogMode, setDealDialogMode] = useState('add');
  const [editingDeal, setEditingDeal] = useState(null);
  const [dealForm, setDealForm] = useState({
    name: '',
    name_urdu: '',
    description: '',
    price: '',
    cost_price: '',
    image: null,
    is_active: true,
    deal_items: [],
  });
  const [dealPreviewImage, setDealPreviewImage] = useState(null);
  const dealFileInputRef = useRef(null);

  // ===== DEAL BUILDER =====
  const [dealBuilderStep, setDealBuilderStep] = useState(0);
  const [dealBuilderCategory, setDealBuilderCategory] = useState('');
  const [dealBuilderItem, setDealBuilderItem] = useState(null);
  const [dealBuilderVariant, setDealBuilderVariant] = useState('');
  const [dealBuilderQuantity, setDealBuilderQuantity] = useState(1);

  // ===== DELETE CONFIRM =====
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, type: '', id: null, name: '' });

  // ==================== IMAGE HANDLING ====================
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ 
        open: true, 
        message: 'Image size should be less than 5MB', 
        severity: 'error' 
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSnackbar({ 
        open: true, 
        message: 'Please select a valid image file', 
        severity: 'error' 
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result);
      setItemForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.onerror = () => {
      setSnackbar({ 
        open: true, 
        message: 'Failed to read image file', 
        severity: 'error' 
      });
    };
    reader.readAsDataURL(file);
  };

  const handleDealImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      setSnackbar({ 
        open: true, 
        message: 'Image size should be less than 5MB', 
        severity: 'error' 
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      setSnackbar({ 
        open: true, 
        message: 'Please select a valid image file', 
        severity: 'error' 
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setDealPreviewImage(reader.result);
      setDealForm((prev) => ({ ...prev, image: reader.result }));
    };
    reader.onerror = () => {
      setSnackbar({ 
        open: true, 
        message: 'Failed to read image file', 
        severity: 'error' 
      });
    };
    reader.readAsDataURL(file);
  };

  const saveImageToDisk = async (base64Data, itemName) => {
    const api = getAPI();
    if (!api) return base64Data;
    
    if (!base64Data || !base64Data.startsWith('data:image')) return base64Data;
    try {
      const fileName = `menu_${Date.now()}_${itemName.replace(/\s+/g, '_').toLowerCase()}.png`;
      const result = await api.saveImage?.(base64Data, fileName);
      return result?.success ? result.path : null;
    } catch (err) {
      console.error('Image save error:', err);
      return null;
    }
  };

  const loadImageUrl = useCallback(async (imagePath, itemId) => {
    const api = getAPI();
    if (!api) return null;
    if (!imagePath) return null;
    if (imagePath.startsWith('data:image')) return imagePath;
    
    try {
      const result = await api.readImage?.(imagePath);
      if (result?.success) {
        setImageUrls((prev) => ({ ...prev, [itemId]: result.data }));
        return result.data;
      }
    } catch (err) {
      console.error('Image read error:', err);
    }
    return null;
  }, []);

  const getImageUrl = (item, isDeal = false) => {
    if (!item.image) return null;
    if (item.image.startsWith('data:image')) return item.image;
    return imageUrls[isDeal ? `deal_${item.id}` : item.id] || null;
  };

  const getCategoryById = (id) => categories.find((c) => c.id === id);
  const getItemById = (id) => menuItems.find((i) => i.id === id);

  // ==================== LOAD DATA ====================
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const api = getAPI();
      
      if (!api) {
        console.error('[MenuManagement] ❌ No API available');
        setSnackbar({ 
          open: true, 
          message: '⚠️ Database not available. Please refresh.', 
          severity: 'warning' 
        });
        setCategories([]);
        setMenuItems([]);
        setDeals([]);
        setLoading(false);
        return;
      }

      console.log('[MenuManagement] 📦 Loading data...');
      
      let cats = [];
      try {
        cats = await api.getCategories?.() || [];
        console.log('[MenuManagement] ✅ Categories loaded:', cats.length);
      } catch (err) {
        console.warn('[MenuManagement] ⚠️ Categories load error:', err.message);
        cats = [];
      }
      
      let items = [];
      try {
        items = await api.getMenuItems?.() || [];
        console.log('[MenuManagement] ✅ Menu items loaded:', items.length);
      } catch (err) {
        console.warn('[MenuManagement] ⚠️ Menu items load error:', err.message);
        items = [];
      }
      
      let dealsData = [];
      try {
        dealsData = await api.getDeals?.() || [];
        console.log('[MenuManagement] ✅ Deals loaded:', dealsData.length);
      } catch (err) {
        console.warn('[MenuManagement] ⚠️ Deals load error:', err.message);
        dealsData = [];
      }
      
      setCategories(cats);
      setMenuItems(items);
      setDeals(Array.isArray(dealsData) ? dealsData : []);
      
      if (cats.length === 0 && items.length === 0 && dealsData.length === 0) {
        setSnackbar({ 
          open: true, 
          message: 'ℹ️ No data found. Start by adding a category!', 
          severity: 'info' 
        });
      }
    } catch (err) {
      console.error('[MenuManagement] ❌ Load error:', err);
      setSnackbar({ 
        open: true, 
        message: `Failed to load menu data: ${err.message}`, 
        severity: 'error' 
      });
      setDeals([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Load images when items change
  useEffect(() => {
    menuItems.forEach((item) => {
      if (item.image && !item.image.startsWith('data:image') && !imageUrls[item.id]) {
        loadImageUrl(item.image, item.id);
      }
    });
  }, [menuItems, imageUrls, loadImageUrl]);

  // Load deal images
  useEffect(() => {
    deals.forEach((deal) => {
      if (deal.image && !deal.image.startsWith('data:image') && !imageUrls[`deal_${deal.id}`]) {
        loadImageUrl(deal.image, `deal_${deal.id}`);
      }
    });
  }, [deals, imageUrls, loadImageUrl]);

  const filteredItems = menuItems.filter((item) => {
    const matchesCategory = selectedCategory === 'all' || item.category_id === selectedCategory;
    const matchesSearch = item.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          item.name_urdu?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const safeDeals = Array.isArray(deals) ? deals : [];
  const filteredDeals = safeDeals.filter((deal) => {
    const matchesSearch = deal.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          deal.name_urdu?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSearch;
  });

  // ==================== CATEGORY CRUD ====================
  const openCategoryDialog = () => {
    setCategoryForm({ name: '', name_urdu: '', type: 'fast_food', color: '#ff5722' });
    setCategoryDialog(true);
  };

  const saveCategory = async () => {
    const api = getAPI();
    if (!api) {
      setSnackbar({ open: true, message: '❌ Database not available', severity: 'error' });
      return;
    }
    
    if (!categoryForm.name.trim()) return;
    try {
      await api.addCategory?.(categoryForm);
      setCategoryDialog(false);
      setSnackbar({ open: true, message: '✅ Category added successfully', severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Category save error:', err);
      setSnackbar({ open: true, message: `❌ Failed to add category: ${err.message}`, severity: 'error' });
    }
  };

  const confirmDeleteCategory = (cat) => {
    setDeleteConfirm({ open: true, type: 'category', id: cat.id, name: cat.name });
  };

  const handleDeleteCategory = async () => {
    const api = getAPI();
    if (!api) return;
    
    try {
      await api.deleteCategory?.(deleteConfirm.id);
      setDeleteConfirm({ ...deleteConfirm, open: false });
      if (selectedCategory === deleteConfirm.id) setSelectedCategory('all');
      setSnackbar({ open: true, message: '🗑️ Category deleted', severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({ open: true, message: `❌ Failed to delete category: ${err.message}`, severity: 'error' });
    }
  };

  // ==================== VARIANTS ====================
  const getSizeTemplate = (categoryType) => {
    return SIZE_TEMPLATES[categoryType] || SIZE_TEMPLATES.default;
  };

  const applySizeTemplate = () => {
    const cat = getCategoryById(itemForm.category_id);
    const template = getSizeTemplate(cat?.type || 'default');
    setItemForm((prev) => ({
      ...prev,
      has_variants: true,
      variants: template.map((t) => ({ ...t })),
      price: '',
    }));
  };

  const addCustomVariant = () => {
    setItemForm((prev) => ({
      ...prev,
      variants: [...prev.variants, { size: '', price: '' }],
    }));
  };

  const updateVariant = (index, field, value) => {
    setItemForm((prev) => {
      const updated = [...prev.variants];
      updated[index][field] = value;
      return { ...prev, variants: updated };
    });
  };

  const removeVariant = (index) => {
    setItemForm((prev) => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index),
    }));
  };

  const toggleVariants = (useVariants) => {
    if (useVariants) {
      applySizeTemplate();
    } else {
      setItemForm((prev) => ({
        ...prev,
        has_variants: false,
        variants: [],
        price: '',
      }));
    }
  };

  // ==================== MODIFIERS ====================
  const loadModifiers = async (itemId) => {
    const api = getAPI();
    if (!api) return;
    
    try {
      const mods = await api.getModifiers?.(itemId);
      setModifiers((mods || []).map((m) => ({ ...m, isExisting: true })));
    } catch (err) {
      console.error('Modifiers load error:', err);
      setModifiers([]);
    }
  };

  const addModifier = () => {
    if (!newModifier.name.trim() || !newModifier.price) return;
    setModifiers((prev) => [...prev, { ...newModifier, id: Date.now(), isNew: true }]);
    setNewModifier({ name: '', price: '' });
  };

  const removeModifier = (index) => {
    setModifiers((prev) => prev.filter((_, i) => i !== index));
  };

  // ==================== ITEM CRUD ====================
  const openItemDialog = async (mode, item = null) => {
    const api = getAPI();
    
    setItemDialogMode(mode);
    if (mode === 'edit' && item) {
      setEditingItem(item);
      
      const variants = parseVariants(item.variants);
      
      setItemForm({
        category_id: item.category_id || '',
        name: item.name || '',
        name_urdu: item.name_urdu || '',
        description: item.description || '',
        price: item.price || '',
        cost_price: item.cost_price || '',
        kitchen_station: item.kitchen_station || 'main',
        prep_time: item.prep_time || 15,
        is_veg: item.is_veg === 1 || item.is_veg === true,
        is_active: item.is_active === 1 || item.is_active === true,
        image: item.image || null,
        has_variants: variants.length > 0,
        variants: variants,
      });
      
      if (item.image) {
        if (item.image.startsWith('data:image')) {
          setPreviewImage(item.image);
        } else if (api) {
          const result = await api.readImage?.(item.image);
          if (result?.success) {
            setPreviewImage(result.data);
          } else {
            setPreviewImage(null);
          }
        }
      } else {
        setPreviewImage(null);
      }
      
      await loadModifiers(item.id);
    } else {
      setEditingItem(null);
      const defaultCat = categories[0];
      setItemForm({
        category_id: selectedCategory !== 'all' ? selectedCategory : (defaultCat?.id || ''),
        name: '',
        name_urdu: '',
        description: '',
        price: '',
        cost_price: '',
        kitchen_station: 'main',
        prep_time: 15,
        is_veg: false,
        is_active: true,
        image: null,
        has_variants: false,
        variants: [],
      });
      setPreviewImage(null);
      setModifiers([]);
    }
    setItemDialog(true);
  };

  const saveItem = async () => {
    const api = getAPI();
    if (!api) {
      setSnackbar({ open: true, message: '❌ Database not available', severity: 'error' });
      return;
    }
    
    if (!itemForm.name.trim()) {
      setSnackbar({ open: true, message: 'Item name is required', severity: 'error' });
      return;
    }
    if (!itemForm.has_variants && !itemForm.price) {
      setSnackbar({ open: true, message: 'Price is required', severity: 'error' });
      return;
    }
    if (itemForm.has_variants && itemForm.variants.some((v) => !v.size.trim() || !v.price)) {
      setSnackbar({ open: true, message: 'All variants must have size and price', severity: 'error' });
      return;
    }

    let imagePath = itemForm.image;
    if (previewImage && previewImage.startsWith('data:image')) {
      const savedPath = await saveImageToDisk(previewImage, itemForm.name);
      if (savedPath) {
        imagePath = savedPath;
      }
    }
    if (itemDialogMode === 'edit' && editingItem && !previewImage?.startsWith('data:image')) {
      imagePath = editingItem.image;
    }

    const payload = {
      ...itemForm,
      price: itemForm.has_variants ? 0 : parseFloat(itemForm.price) || 0,
      cost_price: parseFloat(itemForm.cost_price) || 0,
      is_veg: itemForm.is_veg ? 1 : 0,
      is_active: itemForm.is_active ? 1 : 0,
      prep_time: parseInt(itemForm.prep_time) || 15,
      image: imagePath,
      variants: JSON.stringify(itemForm.variants || []),
    };

    try {
      let itemId;
      if (itemDialogMode === 'edit' && editingItem) {
        await api.updateMenuItem?.(editingItem.id, payload);
        itemId = editingItem.id;
        
        const newModifiers = modifiers.filter((m) => m.isNew);
        for (const mod of newModifiers) {
          await api.addModifier?.({
            item_id: itemId,
            name: mod.name,
            price: parseFloat(mod.price),
          });
        }
      } else {
        const result = await api.addMenuItem?.(payload);
        itemId = result?.lastInsertRowid;
        
        for (const mod of modifiers.filter((m) => m.isNew)) {
          await api.addModifier?.({
            item_id: itemId,
            name: mod.name,
            price: parseFloat(mod.price),
          });
        }
      }

      setItemDialog(false);
      setSnackbar({ open: true, message: `✅ Item ${itemDialogMode === 'edit' ? 'updated' : 'added'} successfully`, severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Item save error:', err);
      setSnackbar({ open: true, message: `❌ Failed to ${itemDialogMode === 'edit' ? 'update' : 'add'} item: ${err.message}`, severity: 'error' });
    }
  };

  const confirmDeleteItem = (item) => {
    setDeleteConfirm({ open: true, type: 'item', id: item.id, name: item.name });
  };

  const handleDeleteItem = async () => {
    const api = getAPI();
    if (!api) return;
    
    try {
      await api.deleteMenuItem?.(deleteConfirm.id);
      setDeleteConfirm({ ...deleteConfirm, open: false });
      setSnackbar({ open: true, message: '✅ Item deleted successfully', severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({ open: true, message: `❌ Failed to delete item: ${err.message}`, severity: 'error' });
    }
  };

  // ==================== DEAL FUNCTIONS ====================
  const getItemsByCategory = (categoryId) => {
    return menuItems.filter(item => item.category_id === categoryId && item.is_active);
  };

  const getItemVariants = (itemId) => {
    const item = getItemById(itemId);
    if (!item) return [];
    return parseVariants(item.variants);
  };

  const handleSelectCategory = (categoryId) => {
    setDealBuilderCategory(categoryId);
    setDealBuilderStep(1);
    setDealBuilderItem(null);
    setDealBuilderVariant('');
  };

  const handleSelectItem = (item) => {
    setDealBuilderItem(item);
    const variants = getItemVariants(item.id);
    if (variants.length > 0) {
      setDealBuilderStep(2);
      setDealBuilderVariant('');
    } else {
      setDealBuilderStep(2);
      setDealBuilderVariant('');
    }
  };

  const handleAddToDeal = () => {
    if (!dealBuilderItem) return;
    
    const item = dealBuilderItem;
    const variants = getItemVariants(item.id);
    let unitPrice = getItemDisplayPrice(item);
    let variantSize = null;
    
    if (variants.length > 0) {
      if (!dealBuilderVariant) {
        setSnackbar({ open: true, message: 'Please select a size', severity: 'warning' });
        return;
      }
      const selectedVar = variants.find(v => v.size === dealBuilderVariant);
      if (selectedVar) {
        unitPrice = parseFloat(selectedVar.price) || 0;
        variantSize = selectedVar.size;
      }
    }
    
    const existingIndex = dealForm.deal_items.findIndex(
      (di) => di.item_id === item.id && di.variant_size === variantSize
    );
    
    if (existingIndex >= 0) {
      const updated = [...dealForm.deal_items];
      updated[existingIndex].quantity += dealBuilderQuantity;
      setDealForm((prev) => ({ ...prev, deal_items: updated }));
    } else {
      setDealForm((prev) => ({
        ...prev,
        deal_items: [
          ...prev.deal_items,
          {
            item_id: item.id,
            item_name: item.name,
            variant_size: variantSize,
            quantity: dealBuilderQuantity,
            unit_price: unitPrice,
          },
        ],
      }));
    }
    
    setDealBuilderStep(0);
    setDealBuilderCategory('');
    setDealBuilderItem(null);
    setDealBuilderVariant('');
    setDealBuilderQuantity(1);
    
    setSnackbar({ open: true, message: `${item.name} added to deal!`, severity: 'success' });
  };

  const removeDealItem = (index) => {
    setDealForm((prev) => ({
      ...prev,
      deal_items: prev.deal_items.filter((_, i) => i !== index),
    }));
  };

  const updateDealItemQuantity = (index, quantity) => {
    if (quantity < 1) return;
    setDealForm((prev) => {
      const updated = [...prev.deal_items];
      updated[index].quantity = quantity;
      return { ...prev, deal_items: updated };
    });
  };

  const openDealDialog = async (mode, deal = null) => {
    const api = getAPI();
    
    setDealDialogMode(mode);
    if (mode === 'edit' && deal) {
      setEditingDeal(deal);
      const dealItems = parseDealItems(deal.deal_items);
      
      const fixedDealItems = dealItems.map(di => {
        const item = getItemById(di.item_id);
        if (item) {
          const variants = parseVariants(item.variants);
          if (di.variant_size && variants.length > 0) {
            const variant = variants.find(v => v.size === di.variant_size);
            if (variant) {
              return { ...di, unit_price: parseFloat(variant.price) || 0 };
            }
          }
          const correctPrice = getItemDisplayPrice(item);
          return { ...di, unit_price: correctPrice > 0 ? correctPrice : (di.unit_price || 0) };
        }
        return di;
      });
      
      setDealForm({
        name: deal.name || '',
        name_urdu: deal.name_urdu || '',
        description: deal.description || '',
        price: deal.price || '',
        cost_price: deal.cost_price || '',
        image: deal.image || null,
        is_active: deal.is_active === 1 || deal.is_active === true,
        deal_items: fixedDealItems,
      });
      if (deal.image) {
        if (deal.image.startsWith('data:image')) {
          setDealPreviewImage(deal.image);
        } else if (api) {
          const result = await api.readImage?.(deal.image);
          setDealPreviewImage(result?.success ? result.data : null);
        }
      } else {
        setDealPreviewImage(null);
      }
    } else {
      setEditingDeal(null);
      setDealForm({
        name: '',
        name_urdu: '',
        description: '',
        price: '',
        cost_price: '',
        image: null,
        is_active: true,
        deal_items: [],
      });
      setDealPreviewImage(null);
    }
    setDealBuilderStep(0);
    setDealBuilderCategory('');
    setDealBuilderItem(null);
    setDealBuilderVariant('');
    setDealBuilderQuantity(1);
    setDealDialog(true);
  };

  const saveDeal = async () => {
    const api = getAPI();
    if (!api) {
      setSnackbar({ open: true, message: '❌ Database not available', severity: 'error' });
      return;
    }
    
    if (!dealForm.name.trim()) {
      setSnackbar({ open: true, message: 'Deal name is required', severity: 'error' });
      return;
    }
    if (!dealForm.price) {
      setSnackbar({ open: true, message: 'Deal price is required', severity: 'error' });
      return;
    }
    if (dealForm.deal_items.length === 0) {
      setSnackbar({ open: true, message: 'Add at least one item to the deal', severity: 'error' });
      return;
    }

    let imagePath = dealForm.image;
    if (dealPreviewImage && dealPreviewImage.startsWith('data:image')) {
      const savedPath = await saveImageToDisk(dealPreviewImage, dealForm.name);
      if (savedPath) imagePath = savedPath;
    }
    if (dealDialogMode === 'edit' && editingDeal && !dealPreviewImage?.startsWith('data:image')) {
      imagePath = editingDeal.image;
    }

    const payload = {
      ...dealForm,
      price: parseFloat(dealForm.price) || 0,
      cost_price: parseFloat(dealForm.cost_price) || 0,
      is_active: dealForm.is_active ? 1 : 0,
      image: imagePath,
      deal_items: JSON.stringify(dealForm.deal_items),
    };

    try {
      if (dealDialogMode === 'edit' && editingDeal) {
        await api.updateDeal?.(editingDeal.id, payload);
      } else {
        await api.addDeal?.(payload);
      }
      setDealDialog(false);
      setSnackbar({ open: true, message: `✅ Deal ${dealDialogMode === 'edit' ? 'updated' : 'added'} successfully`, severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Deal save error:', err);
      setSnackbar({ open: true, message: `❌ Failed to ${dealDialogMode === 'edit' ? 'update' : 'add'} deal: ${err.message}`, severity: 'error' });
    }
  };

  const confirmDeleteDeal = (deal) => {
    setDeleteConfirm({ open: true, type: 'deal', id: deal.id, name: deal.name });
  };

  const handleDeleteDeal = async () => {
    const api = getAPI();
    if (!api) return;
    
    try {
      await api.deleteDeal?.(deleteConfirm.id);
      setDeleteConfirm({ ...deleteConfirm, open: false });
      setSnackbar({ open: true, message: '✅ Deal deleted successfully', severity: 'success' });
      loadData();
    } catch (err) {
      console.error('Delete error:', err);
      setSnackbar({ open: true, message: `❌ Failed to delete deal: ${err.message}`, severity: 'error' });
    }
  };

  const calculateDealOriginalTotal = (dealItems) => {
    return dealItems.reduce((sum, di) => sum + (di.unit_price * di.quantity), 0);
  };

  // ==================== RENDER ITEM CARD ====================
  const renderItemCard = (item) => {
    const cat = getCategoryById(item.category_id);
    const variants = parseVariants(item.variants);
    const hasVariants = variants.length > 0;
    const imageUrl = getImageUrl(item);

    return (
      <Grow in timeout={300}>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              opacity: item.is_active ? 1 : 0.6,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                borderColor: 'transparent',
              },
              overflow: 'hidden',
              height: '100%',
              position: 'relative',
            }}
          >
            {/* Image */}
            <Box 
              sx={{ 
                position: 'relative', 
                width: '100%', 
                height: isMobile ? 160 : 200, 
                flexShrink: 0, 
                bgcolor: '#f8f9fa',
                overflow: 'hidden',
              }}
            >
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={item.name}
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    transition: 'transform 0.5s',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%', 
                  flexDirection: 'column', 
                  gap: 1,
                  bgcolor: '#f5f5f5',
                }}>
                  <ImageIcon sx={{ fontSize: 48, color: 'text.disabled', opacity: 0.3 }} />
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>No Image</Typography>
                </Box>
              )}
              
              <Chip
                size="small"
                label={cat?.name || 'Uncategorized'}
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: `${cat?.color || '#757575'}e0`,
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  backdropFilter: 'blur(4px)',
                }}
              />
              
              {!item.is_active && (
                <Chip
                  size="small"
                  label="Inactive"
                  color="default"
                  sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    right: 10, 
                    fontSize: '0.65rem',
                    backdropFilter: 'blur(4px)',
                  }}
                />
              )}

              {hasVariants && (
                <Chip
                  size="small"
                  label="Sizes"
                  sx={{
                    position: 'absolute',
                    bottom: 10,
                    left: 10,
                    bgcolor: 'rgba(0,0,0,0.7)',
                    color: '#fff',
                    fontWeight: 600,
                    fontSize: '0.6rem',
                    backdropFilter: 'blur(4px)',
                  }}
                />
              )}

              <Box sx={{ 
                position: 'absolute', 
                bottom: 10, 
                right: 10, 
                display: 'flex', 
                gap: 0.5 
              }}>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); openItemDialog('edit', item); }} 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.9)', 
                    '&:hover': { bgcolor: '#fff' }, 
                    width: 32, 
                    height: 32,
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton 
                  size="small" 
                  onClick={(e) => { e.stopPropagation(); confirmDeleteItem(item); }} 
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.9)', 
                    '&:hover': { bgcolor: '#fff' }, 
                    width: 32, 
                    height: 32,
                    color: '#f44336',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
              <Box>
                <Typography 
                  variant="subtitle1" 
                  sx={{ 
                    fontWeight: 700, 
                    color: '#1a1a2e', 
                    fontSize: isMobile ? '0.9rem' : '1rem', 
                    lineHeight: 1.3,
                    display: '-webkit-box',
                    WebkitLineClamp: 1,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.name}
                </Typography>
                {item.name_urdu && (
                  <Typography 
                    variant="caption" 
                    sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}
                  >
                    {item.name_urdu}
                  </Typography>
                )}
              </Box>

              {item.description && (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.75rem', 
                    lineHeight: 1.4, 
                    display: '-webkit-box', 
                    WebkitLineClamp: 2, 
                    WebkitBoxOrient: 'vertical', 
                    overflow: 'hidden',
                    flex: 1,
                  }}
                >
                  {item.description}
                </Typography>
              )}

              {hasVariants ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, mt: 1 }}>
                  {variants.slice(0, isMobile ? 2 : 3).map((v, idx) => (
                    <Chip
                      key={idx}
                      size="small"
                      label={`${v.size}: ${formatPrice(v.price)}`}
                      sx={{ 
                        bgcolor: alpha('#4caf50', 0.1), 
                        color: '#2e7d32', 
                        fontWeight: 600, 
                        fontSize: '0.65rem',
                        height: 22,
                      }}
                    />
                  ))}
                  {variants.length > (isMobile ? 2 : 3) && (
                    <Chip
                      size="small"
                      label={`+${variants.length - (isMobile ? 2 : 3)}`}
                      sx={{ fontSize: '0.6rem', height: 22 }}
                    />
                  )}
                </Box>
              ) : (
                <Chip
                  size="small"
                  icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                  label={formatPrice(item.price)}
                  sx={{ 
                    bgcolor: alpha('#4caf50', 0.1), 
                    color: '#2e7d32', 
                    fontWeight: 700, 
                    fontSize: '0.8rem',
                    width: 'fit-content',
                    height: 26,
                  }}
                />
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 'auto', pt: 1 }}>
                <Chip 
                  size="small" 
                  icon={<KitchenIcon sx={{ fontSize: 14 }} />} 
                  label={item.kitchen_station} 
                  variant="outlined" 
                  sx={{ fontSize: '0.6rem', height: 22 }} 
                />
                <Chip 
                  size="small" 
                  icon={<TimerIcon sx={{ fontSize: 14 }} />} 
                  label={`${item.prep_time}m`} 
                  variant="outlined" 
                  sx={{ fontSize: '0.6rem', height: 22 }} 
                />
                {item.is_veg === 1 && (
                  <Chip 
                    size="small" 
                    label="Veg" 
                    color="success" 
                    sx={{ fontSize: '0.6rem', height: 22 }} 
                  />
                )}
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grow>
    );
  };

  // ==================== RENDER DEAL CARD ====================
  const renderDealCard = (deal) => {
    const dealItems = parseDealItems(deal.deal_items);
    const imageUrl = getImageUrl(deal, true);
    const originalTotal = calculateDealOriginalTotal(dealItems);
    const savings = originalTotal - (deal.price || 0);

    return (
      <Grow in timeout={300}>
        <Grid item xs={12} sm={6} md={4} lg={3} xl={2.4}>
          <Paper
            elevation={0}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              opacity: deal.is_active ? 1 : 0.6,
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-6px)',
                boxShadow: '0 12px 40px rgba(255,107,53,0.15)',
                borderColor: alpha('#ff6b35', 0.3),
              },
              overflow: 'hidden',
              height: '100%',
              position: 'relative',
              bgcolor: alpha('#ff6b35', 0.03),
            }}
          >
            {/* Image */}
            <Box sx={{ 
              position: 'relative', 
              width: '100%', 
              height: isMobile ? 160 : 200, 
              flexShrink: 0, 
              bgcolor: '#f8f9fa',
              overflow: 'hidden',
            }}>
              {imageUrl ? (
                <Box
                  component="img"
                  src={imageUrl}
                  alt={deal.name}
                  sx={{ 
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    transition: 'transform 0.5s',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                />
              ) : (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center', 
                  height: '100%', 
                  flexDirection: 'column', 
                  gap: 1,
                  bgcolor: alpha('#ff6b35', 0.05),
                }}>
                  <BasketIcon sx={{ fontSize: 48, color: '#ff6b35', opacity: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>No Image</Typography>
                </Box>
              )}
              
              <Chip
                icon={<DealIcon sx={{ fontSize: 14 }} />}
                label="DEAL"
                sx={{
                  position: 'absolute',
                  top: 10,
                  left: 10,
                  bgcolor: '#ff6b35',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: '0.65rem',
                  boxShadow: '0 4px 12px rgba(255,107,53,0.3)',
                }}
              />
              
              {!deal.is_active && (
                <Chip
                  size="small"
                  label="Inactive"
                  color="default"
                  sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    right: 10, 
                    fontSize: '0.65rem',
                    backdropFilter: 'blur(4px)',
                  }}
                />
              )}

              <Box sx={{ 
                position: 'absolute', 
                bottom: 10, 
                right: 10, 
                display: 'flex', 
                gap: 0.5 
              }}>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); openDealDialog('edit', deal); }}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.9)', 
                    '&:hover': { bgcolor: '#fff' }, 
                    width: 32, 
                    height: 32,
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => { e.stopPropagation(); confirmDeleteDeal(deal); }}
                  sx={{ 
                    bgcolor: 'rgba(255,255,255,0.9)', 
                    '&:hover': { bgcolor: '#fff' }, 
                    width: 32, 
                    height: 32,
                    color: '#f44336',
                    backdropFilter: 'blur(4px)',
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', p: 2, gap: 1 }}>
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontWeight: 700, 
                  color: '#1a1a2e', 
                  fontSize: isMobile ? '0.9rem' : '1rem', 
                  lineHeight: 1.3,
                  display: '-webkit-box',
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {deal.name}
              </Typography>
              {deal.name_urdu && (
                <Typography 
                  variant="caption" 
                  sx={{ color: 'text.secondary', display: 'block', fontSize: '0.7rem' }}
                >
                  {deal.name_urdu}
                </Typography>
              )}

              {/* Deal Items */}
              <Box sx={{ 
                bgcolor: '#fff', 
                borderRadius: 2, 
                p: 1.5, 
                border: '1px solid', 
                borderColor: 'divider',
                flex: 1,
              }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color: '#ff6b35', display: 'block', mb: 0.5, fontSize: '0.7rem' }}>
                  Includes:
                </Typography>
                {dealItems.slice(0, isMobile ? 2 : 3).map((di, idx) => (
                  <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.3 }}>
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem' }}>
                      {di.quantity}x {di.item_name} {di.variant_size ? `(${di.variant_size})` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', textDecoration: 'line-through', fontSize: '0.65rem' }}>
                      {formatPrice(di.unit_price * di.quantity)}
                    </Typography>
                  </Box>
                ))}
                {dealItems.length > (isMobile ? 2 : 3) && (
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    +{dealItems.length - (isMobile ? 2 : 3)} more items
                  </Typography>
                )}
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }}>
                    Original:
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.disabled', textDecoration: 'line-through', fontWeight: 600, fontSize: '0.65rem' }}>
                    {formatPrice(originalTotal)}
                  </Typography>
                </Box>
              </Box>

              {/* Price */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 'auto', pt: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Chip
                    size="small"
                    icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                    label={formatPrice(deal.price)}
                    sx={{ 
                      bgcolor: '#ff6b35', 
                      color: '#fff', 
                      fontWeight: 700, 
                      fontSize: isMobile ? '0.75rem' : '0.85rem',
                      height: 28,
                    }}
                  />
                  {savings > 0 && (
                    <Chip
                      size="small"
                      label={`Save ${formatPrice(savings)}`}
                      sx={{ 
                        bgcolor: alpha('#4caf50', 0.1), 
                        color: '#2e7d32', 
                        fontWeight: 600, 
                        fontSize: '0.6rem',
                        height: 22,
                      }}
                    />
                  )}
                </Box>
              </Box>
              {deal.description && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary', 
                    fontSize: '0.7rem',
                    display: '-webkit-box', 
                    WebkitLineClamp: 1, 
                    WebkitBoxOrient: 'vertical', 
                    overflow: 'hidden',
                  }}
                >
                  {deal.description}
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grow>
    );
  };

  // ==================== MAIN RETURN ====================
  return (
    <Box sx={{ 
      p: isMobile ? 1.5 : isTablet ? 2 : 3,
      maxWidth: 1600,
      mx: 'auto',
      width: '100%',
      pb: isMobile ? 8 : 3,
    }}>
      {/* Mode Indicator */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
        <Chip
          size="small"
          label={window.electronAPI ? '⚡ Desktop Mode' : '🌐 Browser Mode'}
          sx={{
            bgcolor: window.electronAPI ? alpha('#1976d2', 0.1) : alpha('#4caf50', 0.1),
            color: window.electronAPI ? '#1976d2' : '#4caf50',
            fontWeight: 600,
          }}
        />
      </Box>

      {/* HEADER - RESPONSIVE */}
      <Box sx={{ 
        mb: 2, 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        gap: 2 
      }}>
        <Box>
          <Typography variant={isMobile ? "h5" : "h4"} sx={{ fontWeight: 700, color: '#1a1a2e', mb: 0.5 }}>
            Menu Management
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            مینو مینجمنٹ | Categories, Items, Deals & Modifiers
          </Typography>
        </Box>
        <Box sx={{ 
          display: 'flex', 
          gap: 1.5, 
          flexWrap: 'wrap',
          flexDirection: isMobile ? 'column' : 'row',
        }}>
          {isMobile && (
            <Button
              variant="outlined"
              startIcon={<MenuIcon />}
              onClick={() => setMobileDrawer(true)}
              sx={{ flex: 1 }}
            >
              Menu
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            disabled={loading}
            sx={{ 
              borderRadius: 2, 
              fontWeight: 600,
              flex: isMobile ? 1 : 'none',
            }}
          >
            Refresh
          </Button>
          
          <Button
            variant="outlined"
            startIcon={<CategoryIcon />}
            onClick={openCategoryDialog}
            sx={{ 
              borderRadius: 2, 
              fontWeight: 600,
              flex: isMobile ? 1 : 'none',
            }}
          >
            Add Category
          </Button>
          {activeTab === 0 ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openItemDialog('add')}
              sx={{ 
                bgcolor: '#ff6b35', 
                '&:hover': { bgcolor: '#e55a2b' }, 
                fontWeight: 600, 
                borderRadius: 2,
                flex: isMobile ? 1 : 'none',
              }}
            >
              {isMobile ? 'Add Item' : 'Add Item'}
            </Button>
          ) : (
            <Button
              variant="contained"
              startIcon={<DealIcon />}
              onClick={() => openDealDialog('add')}
              sx={{ 
                bgcolor: '#ff6b35', 
                '&:hover': { bgcolor: '#e55a2b' }, 
                fontWeight: 600, 
                borderRadius: 2,
                flex: isMobile ? 1 : 'none',
              }}
            >
              {isMobile ? 'Add Deal' : 'Add Deal'}
            </Button>
          )}
        </Box>
      </Box>

      {/* TABS - RESPONSIVE */}
      <Paper sx={{ 
        borderRadius: 3, 
        mb: 2, 
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        overflow: 'hidden',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.paper',
      }}>
        <Tabs
          value={activeTab}
          onChange={(e, v) => setActiveTab(v)}
          sx={{
            '& .MuiTabs-flexContainer': { px: isMobile ? 0 : 2, pt: 1 },
            '& .MuiTabs-indicator': { bgcolor: '#ff6b35', height: 3 },
          }}
          variant={isMobile ? "fullWidth" : "standard"}
        >
          <Tab
            icon={<RestaurantIcon fontSize="small" />}
            iconPosition={isMobile ? "top" : "start"}
            label={isMobile ? `Items (${menuItems.length})` : `Items (${menuItems.length})`}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: isMobile ? 1 : 1.5,
              color: activeTab === 0 ? '#ff6b35 !important' : 'text.secondary',
            }}
          />
          <Tab
            icon={<DealIcon fontSize="small" />}
            iconPosition={isMobile ? "top" : "start"}
            label={isMobile ? `Deals (${safeDeals.length})` : `Deals (${safeDeals.length})`}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: isMobile ? '0.75rem' : '0.875rem',
              py: isMobile ? 1 : 1.5,
              color: activeTab === 1 ? '#ff6b35 !important' : 'text.secondary',
            }}
          />
        </Tabs>
      </Paper>

      {/* CATEGORIES ROW - RESPONSIVE */}
      <Paper sx={{ 
        borderRadius: 3, 
        overflow: 'hidden', 
        boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
        mb: 2,
        p: 1.5,
      }}>
        <Box sx={{ 
          display: 'flex', 
          gap: 1.2, 
          overflowX: 'auto',
          pb: 1,
          '&::-webkit-scrollbar': { height: 4 },
          '&::-webkit-scrollbar-track': { bgcolor: '#f0f0f0', borderRadius: 2 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#ccc', borderRadius: 2 }
        }}>
          <Button
            size="small"
            variant={selectedCategory === 'all' ? 'contained' : 'outlined'}
            onClick={() => setSelectedCategory('all')}
            startIcon={<RestaurantIcon fontSize="small" />}
            sx={{ 
              borderRadius: 4, 
              bgcolor: selectedCategory === 'all' ? '#1a1a2e' : 'transparent',
              borderColor: '#1a1a2e',
              color: selectedCategory === 'all' ? '#fff' : '#1a1a2e',
              whiteSpace: 'nowrap',
              px: 2,
              py: 0.6,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: isMobile ? '0.7rem' : '0.85rem',
              '&:hover': {
                bgcolor: selectedCategory === 'all' ? '#1a1a2e' : 'rgba(26,26,46,0.08)'
              }
            }}
          >
            All ({menuItems.length})
          </Button>

          {categories.map((cat) => {
            const itemCount = menuItems.filter((i) => i.category_id === cat.id).length;
            const isSelected = selectedCategory === cat.id;
            return (
              <Button
                key={cat.id}
                size="small"
                variant={isSelected ? 'contained' : 'outlined'}
                onClick={() => setSelectedCategory(cat.id)}
                startIcon={CATEGORY_ICONS[cat.type] || CATEGORY_ICONS.default}
                endIcon={
                  isDesktop && (
                    <IconButton 
                      size="small" 
                      onClick={(e) => { e.stopPropagation(); confirmDeleteCategory(cat); }}
                      sx={{ 
                        color: isSelected ? 'rgba(255,255,255,0.7)' : 'text.disabled',
                        p: 0.3,
                        ml: 0.5,
                        '&:hover': { color: '#ff4444' }
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  )
                }
                sx={{ 
                  borderRadius: 4, 
                  bgcolor: isSelected ? (cat.color || '#ff6b35') : 'transparent',
                  borderColor: cat.color || '#ff6b35',
                  color: isSelected ? '#fff' : (cat.color || '#ff6b35'),
                  whiteSpace: 'nowrap',
                  px: 2,
                  py: 0.6,
                  fontWeight: 600,
                  textTransform: 'none',
                  fontSize: isMobile ? '0.7rem' : '0.85rem',
                  '&:hover': {
                    bgcolor: isSelected ? (cat.color || '#ff6b35') : `${cat.color || '#ff6b35'}10`
                  }
                }}
              >
                {isMobile ? `${cat.name.substring(0, 8)} (${itemCount})` : `${cat.name} (${itemCount})`}
              </Button>
            );
          })}
        </Box>
      </Paper>

      {/* SEARCH BAR - RESPONSIVE */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: isMobile ? 'column' : 'row',
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'stretch' : 'center', 
        mb: 2, 
        gap: 2 
      }}>
        <TextField
          size="small"
          placeholder={activeTab === 0 ? "Search items..." : "Search deals..."}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ 
            width: isMobile ? '100%' : 300,
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              bgcolor: 'background.paper',
            }
          }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>,
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Grid View">
            <IconButton 
              size="small" 
              onClick={() => setViewMode('grid')}
              sx={{ 
                bgcolor: viewMode === 'grid' ? alpha('#ff6b35', 0.1) : 'transparent',
                color: viewMode === 'grid' ? '#ff6b35' : 'text.secondary',
              }}
            >
              <GridViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="List View">
            <IconButton 
              size="small" 
              onClick={() => setViewMode('list')}
              sx={{ 
                bgcolor: viewMode === 'list' ? alpha('#ff6b35', 0.1) : 'transparent',
                color: viewMode === 'list' ? '#ff6b35' : 'text.secondary',
              }}
            >
              <ListViewIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* CONTENT */}
      {loading ? (
        <Paper sx={{ p: 3, borderRadius: 3 }}>
          <Grid container spacing={2}>
            {[...Array(8)].map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Skeleton variant="rectangular" height={250} sx={{ borderRadius: 3 }} />
                <Skeleton variant="text" sx={{ mt: 1 }} />
                <Skeleton variant="text" width="60%" />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ) : activeTab === 0 ? (
        filteredItems.length > 0 ? (
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {filteredItems.map((item) => renderItemCard(item))}
          </Grid>
        ) : (
          <Paper sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
            <RestaurantIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              No items found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
              اس کیٹیگری میں کوئی آئٹم نہیں
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<AddIcon />} 
              onClick={() => openItemDialog('add')} 
              sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}
            >
              Add First Item
            </Button>
          </Paper>
        )
      ) : (
        filteredDeals.length > 0 ? (
          <Grid container spacing={isMobile ? 1.5 : 2}>
            {filteredDeals.map((deal) => renderDealCard(deal))}
          </Grid>
        ) : (
          <Paper sx={{ textAlign: 'center', py: 8, borderRadius: 3 }}>
            <DealIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
              No deals found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.disabled', mb: 3 }}>
              کوئی ڈیل موجود نہیں
            </Typography>
            <Button 
              variant="contained" 
              startIcon={<DealIcon />} 
              onClick={() => openDealDialog('add')} 
              sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}
            >
              Add First Deal
            </Button>
          </Paper>
        )
      )}

      {/* ===== CATEGORY DIALOG - RESPONSIVE ===== */}
      <Dialog 
        open={categoryDialog} 
        onClose={() => setCategoryDialog(false)} 
        maxWidth="xs" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
          } 
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          Add Category / نیا کیٹیگری
        </DialogTitle>
        <DialogContent>
          <TextField 
            fullWidth 
            label="Category Name" 
            margin="normal" 
            value={categoryForm.name} 
            onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })} 
            autoFocus 
            size={isMobile ? "small" : "medium"}
          />
          <TextField 
            fullWidth 
            label="Name in Urdu / اردو نام" 
            margin="normal" 
            value={categoryForm.name_urdu} 
            onChange={(e) => setCategoryForm({ ...categoryForm, name_urdu: e.target.value })} 
            size={isMobile ? "small" : "medium"}
          />
          <FormControl fullWidth margin="normal" size={isMobile ? "small" : "medium"}>
            <InputLabel>Type</InputLabel>
            <Select 
              value={categoryForm.type} 
              label="Type" 
              onChange={(e) => setCategoryForm({ ...categoryForm, type: e.target.value, color: CATEGORY_COLORS[e.target.value] || '#757575' })}
            >
              <MenuItem value="fast_food">Fast Food</MenuItem>
              <MenuItem value="desi">Desi Food</MenuItem>
              <MenuItem value="bbq">BBQ</MenuItem>
              <MenuItem value="drinks">Drinks</MenuItem>
              <MenuItem value="pizza">Pizza</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2">Color:</Typography>
            <Box sx={{ width: 32, height: 32, borderRadius: 1, bgcolor: categoryForm.color, border: '1px solid #ddd' }} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setCategoryDialog(false)} variant="outlined" fullWidth={isMobile}>Cancel</Button>
          <Button 
            onClick={saveCategory} 
            variant="contained" 
            startIcon={<SaveIcon />} 
            disabled={!categoryForm.name.trim()} 
            sx={{ bgcolor: '#ff6b35', borderRadius: 2 }}
            fullWidth={isMobile}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== ITEM DIALOG - RESPONSIVE ===== */}
      <Dialog 
        open={itemDialog} 
        onClose={() => setItemDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
          } 
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e' }}>
          {itemDialogMode === 'add' ? 'Add Menu Item' : 'Edit Menu Item'} / {itemDialogMode === 'add' ? 'نیا آئٹم' : 'آئٹم ترمیم'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Image Upload */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => fileInputRef.current?.click()}
                sx={{
                  height: isMobile ? 150 : 200,
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: previewImage ? '#ff6b35' : 'divider',
                  bgcolor: previewImage ? 'transparent' : '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#ff6b35', bgcolor: '#fff3e0' },
                }}
              >
                {previewImage ? (
                  <Box component="img" src={previewImage} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: isMobile ? 32 : 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      Upload Image
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      تصویر اپلوڈ کریں
                    </Typography>
                  </>
                )}
                <input type="file" accept="image/*" hidden ref={fileInputRef} onChange={handleImageSelect} />
              </Box>
              {previewImage && (
                <Button 
                  size="small" 
                  fullWidth 
                  sx={{ mt: 1, fontSize: isMobile ? '0.7rem' : '0.8rem' }} 
                  onClick={() => { setPreviewImage(null); setItemForm((prev) => ({ ...prev, image: null })); }}
                >
                  Remove Image
                </Button>
              )}
            </Grid>

            {/* Form Fields */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={isMobile ? 1.5 : 2}>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Item Name *" 
                    value={itemForm.name} 
                    onChange={(e) => setItemForm((prev) => ({ ...prev, name: e.target.value }))} 
                    required 
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Name in Urdu / اردو نام" 
                    value={itemForm.name_urdu} 
                    onChange={(e) => setItemForm((prev) => ({ ...prev, name_urdu: e.target.value }))} 
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                    <InputLabel>Category *</InputLabel>
                    <Select 
                      value={itemForm.category_id} 
                      label="Category" 
                      onChange={(e) => setItemForm((prev) => ({ ...prev, category_id: e.target.value }))}
                    >
                      {categories.map((c) => (
                        <MenuItem key={c.id} value={c.id}>{c.name} ({c.name_urdu})</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size={isMobile ? "small" : "medium"}>
                    <InputLabel>Kitchen Station</InputLabel>
                    <Select 
                      value={itemForm.kitchen_station} 
                      label="Kitchen Station" 
                      onChange={(e) => setItemForm((prev) => ({ ...prev, kitchen_station: e.target.value }))}
                    >
                      {KITCHEN_STATIONS.map((s) => <MenuItem key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField 
                    fullWidth 
                    label="Prep Time (minutes)" 
                    type="number" 
                    value={itemForm.prep_time} 
                    onChange={(e) => setItemForm((prev) => ({ ...prev, prep_time: e.target.value }))} 
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 0.5 : 3, mt: 1 }}>
                    <FormControlLabel 
                      control={<Switch checked={itemForm.is_veg} onChange={(e) => setItemForm((prev) => ({ ...prev, is_veg: e.target.checked }))} />} 
                      label={<Typography variant="body2">Vegetarian</Typography>}
                    />
                    <FormControlLabel 
                      control={<Switch checked={itemForm.is_active} onChange={(e) => setItemForm((prev) => ({ ...prev, is_active: e.target.checked }))} />} 
                      label={<Typography variant="body2">Active</Typography>}
                    />
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <TextField 
                    fullWidth 
                    label="Description" 
                    multiline 
                    rows={isMobile ? 2 : 3} 
                    value={itemForm.description} 
                    onChange={(e) => setItemForm((prev) => ({ ...prev, description: e.target.value }))} 
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Variants */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 2, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? '1rem' : '1.25rem' }}>
                  Pricing / قیمت
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={itemForm.has_variants}
                      onChange={(e) => toggleVariants(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2">Multiple Sizes / سائزز</Typography>}
                />
              </Box>

              {itemForm.has_variants ? (
                <Box>
                  <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 2, gap: 1 }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                      {getCategoryById(itemForm.category_id)?.type === 'pizza' && 'Pizza Sizes: Small, Regular, Large, XL, Jumbo'}
                      {getCategoryById(itemForm.category_id)?.type === 'burger' && 'Burger Sizes: Regular, Large, Double'}
                      {getCategoryById(itemForm.category_id)?.type === 'bbq' && 'BBQ: Half, Full'}
                      {getCategoryById(itemForm.category_id)?.type === 'desi' && 'Desi: Half, Full, Double'}
                      {getCategoryById(itemForm.category_id)?.type === 'drinks' && 'Drinks: Small, Regular, Large, Jumbo'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<AddCircleIcon />}
                      onClick={addCustomVariant}
                      sx={{ borderRadius: 2 }}
                    >
                      Add Size
                    </Button>
                  </Box>
                  <Grid container spacing={isMobile ? 1 : 2}>
                    {itemForm.variants.map((variant, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
                              Size {index + 1}
                            </Typography>
                            <IconButton size="small" onClick={() => removeVariant(index)} sx={{ color: '#f44336' }}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <TextField
                            fullWidth
                            size="small"
                            label="Size Name"
                            placeholder="e.g. Regular"
                            value={variant.size}
                            onChange={(e) => updateVariant(index, 'size', e.target.value)}
                            sx={{ mb: 1 }}
                          />
                          <TextField
                            fullWidth
                            size="small"
                            label="Price (PKR)"
                            type="number"
                            placeholder="0"
                            value={variant.price}
                            onChange={(e) => updateVariant(index, 'price', e.target.value)}
                            InputProps={{
                              startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" /></InputAdornment>,
                            }}
                          />
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              ) : (
                <Grid container spacing={isMobile ? 1.5 : 2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Price (PKR) *"
                      type="number"
                      value={itemForm.price}
                      onChange={(e) => setItemForm((prev) => ({ ...prev, price: e.target.value }))}
                      required
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" /></InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Cost Price (PKR)"
                      type="number"
                      value={itemForm.cost_price}
                      onChange={(e) => setItemForm((prev) => ({ ...prev, cost_price: e.target.value }))}
                      size={isMobile ? "small" : "medium"}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" /></InputAdornment>,
                      }}
                    />
                  </Grid>
                </Grid>
              )}
            </Grid>

            {/* Modifiers */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 2, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                Modifiers / ایڈ اونز
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 2, mb: 2 }}>
                <TextField
                  size="small"
                  label="Modifier Name"
                  placeholder="e.g. Extra Cheese"
                  value={newModifier.name}
                  onChange={(e) => setNewModifier((prev) => ({ ...prev, name: e.target.value }))}
                  sx={{ flex: 1, minWidth: isMobile ? '100%' : 150 }}
                />
                <TextField
                  size="small"
                  label="Price"
                  type="number"
                  placeholder="0"
                  value={newModifier.price}
                  onChange={(e) => setNewModifier((prev) => ({ ...prev, price: e.target.value }))}
                  sx={{ width: isMobile ? '100%' : 120 }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">PKR</InputAdornment>,
                  }}
                />
                <Button
                  variant="contained"
                  startIcon={<AddCircleIcon />}
                  onClick={addModifier}
                  disabled={!newModifier.name.trim() || !newModifier.price}
                  sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2 }}
                >
                  Add
                </Button>
              </Box>
              {modifiers.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {modifiers.map((mod, idx) => (
                    <Chip
                      key={idx}
                      label={`${mod.name} (+${formatPrice(mod.price)})`}
                      onDelete={() => removeModifier(idx)}
                      deleteIcon={<DeleteIcon />}
                      sx={{
                        bgcolor: '#fff3e0',
                        color: '#e65100',
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': { color: '#e65100' },
                      }}
                    />
                  ))}
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setItemDialog(false)} variant="outlined" sx={{ borderRadius: 2 }} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={saveItem}
            variant="contained"
            startIcon={<SaveIcon />}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2, fontWeight: 600 }}
            fullWidth={isMobile}
          >
            {itemDialogMode === 'add' ? 'Add Item' : 'Update Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DEAL DIALOG - RESPONSIVE ===== */}
      <Dialog 
        open={dealDialog} 
        onClose={() => setDealDialog(false)} 
        maxWidth="md" 
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ 
          sx: { 
            borderRadius: isMobile ? 0 : 3,
            m: isMobile ? 0 : 2,
          } 
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#1a1a2e', pb: 1 }}>
          {dealDialogMode === 'add' ? 'Add Deal' : 'Edit Deal'} / {dealDialogMode === 'add' ? 'نیا ڈیل' : 'ڈیل ترمیم'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={isMobile ? 2 : 3}>
            {/* Deal Image Upload */}
            <Grid item xs={12} md={4}>
              <Box
                onClick={() => dealFileInputRef.current?.click()}
                sx={{
                  height: isMobile ? 150 : 200,
                  borderRadius: 3,
                  border: '2px dashed',
                  borderColor: dealPreviewImage ? '#ff6b35' : 'divider',
                  bgcolor: dealPreviewImage ? 'transparent' : '#f8f9fa',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s',
                  '&:hover': { borderColor: '#ff6b35', bgcolor: '#fff3e0' },
                }}
              >
                {dealPreviewImage ? (
                  <Box component="img" src={dealPreviewImage} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  <>
                    <UploadIcon sx={{ fontSize: isMobile ? 32 : 40, color: 'text.disabled', mb: 1 }} />
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: isMobile ? '0.8rem' : '1rem' }}>
                      Upload Image
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                      تصویر اپلوڈ کریں
                    </Typography>
                  </>
                )}
                <input type="file" accept="image/*" hidden ref={dealFileInputRef} onChange={handleDealImageSelect} />
              </Box>
              {dealPreviewImage && (
                <Button 
                  size="small" 
                  fullWidth 
                  sx={{ mt: 1, fontSize: isMobile ? '0.7rem' : '0.8rem' }} 
                  onClick={() => { setDealPreviewImage(null); setDealForm((prev) => ({ ...prev, image: null })); }}
                >
                  Remove Image
                </Button>
              )}
            </Grid>

            {/* Deal Basic Info */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={isMobile ? 1.5 : 2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Deal Name *"
                    value={dealForm.name}
                    onChange={(e) => setDealForm((prev) => ({ ...prev, name: e.target.value }))}
                    required
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name in Urdu / اردو نام"
                    value={dealForm.name_urdu}
                    onChange={(e) => setDealForm((prev) => ({ ...prev, name_urdu: e.target.value }))}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Deal Price (PKR) *"
                    type="number"
                    value={dealForm.price}
                    onChange={(e) => setDealForm((prev) => ({ ...prev, price: e.target.value }))}
                    required
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Cost Price (PKR)"
                    type="number"
                    value={dealForm.cost_price}
                    onChange={(e) => setDealForm((prev) => ({ ...prev, cost_price: e.target.value }))}
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      startAdornment: <InputAdornment position="start"><MoneyIcon fontSize="small" /></InputAdornment>,
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Description"
                    multiline
                    rows={isMobile ? 2 : 3}
                    value={dealForm.description}
                    onChange={(e) => setDealForm((prev) => ({ ...prev, description: e.target.value }))}
                    size={isMobile ? "small" : "medium"}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={dealForm.is_active}
                        onChange={(e) => setDealForm((prev) => ({ ...prev, is_active: e.target.checked }))}
                      />
                    }
                    label={<Typography variant="body2">Active / فعال</Typography>}
                  />
                </Grid>
              </Grid>
            </Grid>

            {/* Deal Items Builder */}
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', mb: 2, gap: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? '1rem' : '1.25rem' }}>
                  Deal Items / ڈیل آئٹمز
                </Typography>
                <Chip
                  label={`${dealForm.deal_items.length} items added`}
                  size="small"
                  sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600 }}
                />
              </Box>

              {/* Stepper */}
              <Stepper 
                activeStep={dealBuilderStep} 
                sx={{ 
                  mb: 3,
                  '& .MuiStepLabel-label': {
                    fontSize: isMobile ? '0.65rem' : '0.75rem',
                  }
                }}
              >
                {dealBuilderSteps.map((label, index) => (
                  <Step key={label}>
                    <StepLabel
                      StepIconProps={{
                        sx: {
                          '&.Mui-active': { color: '#ff6b35' },
                          '&.Mui-completed': { color: '#ff6b35' },
                        },
                      }}
                    >
                      {isMobile ? `Step ${index + 1}` : label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>

              {/* STEP 0 */}
              {dealBuilderStep === 0 && (
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    Select a category to browse items / کیٹیگری منتخب کریں
                  </Typography>
                  <Grid container spacing={isMobile ? 1 : 2}>
                    {categories.map((cat) => {
                      const catItems = getItemsByCategory(cat.id);
                      return (
                        <Grid item xs={6} sm={4} md={3} key={cat.id}>
                          <Card
                            onClick={() => handleSelectCategory(cat.id)}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 3,
                              border: '2px solid',
                              borderColor: dealBuilderCategory === cat.id ? cat.color : 'transparent',
                              bgcolor: dealBuilderCategory === cat.id ? `${cat.color}10` : '#fff',
                              transition: 'all 0.2s',
                              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
                            }}
                          >
                            <CardContent sx={{ textAlign: 'center', py: isMobile ? 2 : 3 }}>
                              <Avatar sx={{ bgcolor: `${cat.color}20`, color: cat.color, width: isMobile ? 40 : 56, height: isMobile ? 40 : 56, mx: 'auto', mb: 1 }}>
                                {CATEGORY_ICONS[cat.type] || CATEGORY_ICONS.default}
                              </Avatar>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? '0.7rem' : '0.9rem' }}>
                                {cat.name}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.6rem' : '0.75rem' }}>
                                {catItems.length} items
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Box>
              )}

              {/* STEP 1 */}
              {dealBuilderStep === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => { setDealBuilderStep(0); setDealBuilderCategory(''); }}
                      sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                    >
                      Back
                    </Button>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                      | {getCategoryById(dealBuilderCategory)?.name}
                    </Typography>
                  </Box>
                  <Grid container spacing={isMobile ? 1 : 2}>
                    {getItemsByCategory(dealBuilderCategory).map((item) => {
                      const imageUrl = getImageUrl(item);
                      const variants = getItemVariants(item.id);
                      const displayPrice = getItemDisplayPrice(item);
                      return (
                        <Grid item xs={6} sm={4} md={3} key={item.id}>
                          <Card
                            onClick={() => handleSelectItem(item)}
                            sx={{
                              cursor: 'pointer',
                              borderRadius: 3,
                              border: '2px solid',
                              borderColor: dealBuilderItem?.id === item.id ? '#ff6b35' : 'transparent',
                              transition: 'all 0.2s',
                              '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 8px 24px rgba(0,0,0,0.12)' },
                            }}
                          >
                            <CardActionArea>
                              <Box sx={{ 
                                height: isMobile ? 80 : 120, 
                                bgcolor: '#f5f5f5', 
                                position: 'relative', 
                                overflow: 'hidden' 
                              }}>
                                {imageUrl ? (
                                  <Box component="img" src={imageUrl} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                ) : (
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <RestaurantIcon sx={{ fontSize: isMobile ? 28 : 40, color: 'text.disabled' }} />
                                  </Box>
                                )}
                              </Box>
                              <CardContent sx={{ py: isMobile ? 1 : 1.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                                  {item.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>
                                  {item.name_urdu}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                                  <Chip
                                    size="small"
                                    label={formatPrice(displayPrice)}
                                    sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.6rem', height: 20 }}
                                  />
                                  {variants.length > 0 && (
                                    <Chip
                                      size="small"
                                      label={`${variants.length} sizes`}
                                      variant="outlined"
                                      sx={{ fontSize: '0.6rem', height: 20 }}
                                    />
                                  )}
                                </Box>
                              </CardContent>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                  {getItemsByCategory(dealBuilderCategory).length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        No active items in this category / اس کیٹیگری میں کوئی آئٹم نہیں
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              {/* STEP 2 */}
              {dealBuilderStep === 2 && dealBuilderItem && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Button
                      size="small"
                      startIcon={<ArrowBackIcon />}
                      onClick={() => { setDealBuilderStep(1); setDealBuilderItem(null); }}
                      sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.8rem' }}
                    >
                      Back
                    </Button>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.85rem' }}>
                      | {dealBuilderItem.name}
                    </Typography>
                  </Box>

                  <Paper sx={{ p: isMobile ? 2 : 3, borderRadius: 3, bgcolor: '#fafafa' }}>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 2, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                      {dealBuilderItem.name}
                    </Typography>

                    {/* Variant Selection */}
                    {getItemVariants(dealBuilderItem.id).length > 0 && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                          Select Size / سائز منتخب کریں *
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                          {getItemVariants(dealBuilderItem.id).map((variant) => (
                            <Button
                              key={variant.size}
                              variant={dealBuilderVariant === variant.size ? 'contained' : 'outlined'}
                              onClick={() => setDealBuilderVariant(variant.size)}
                              sx={{
                                borderRadius: 2,
                                fontWeight: 600,
                                fontSize: isMobile ? '0.7rem' : '0.8rem',
                                ...(dealBuilderVariant === variant.size
                                  ? { bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' } }
                                  : { borderColor: 'divider', color: 'text.primary' }
                                ),
                              }}
                            >
                              {variant.size} — {formatPrice(variant.price)}
                            </Button>
                          ))}
                        </Box>
                      </Box>
                    )}

                    {/* Quantity */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                        Quantity / مقدار
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <IconButton
                          onClick={() => setDealBuilderQuantity(Math.max(1, dealBuilderQuantity - 1))}
                          sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}
                          size={isMobile ? "small" : "medium"}
                        >
                          <RemoveIcon />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, minWidth: 40, textAlign: 'center', fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
                          {dealBuilderQuantity}
                        </Typography>
                        <IconButton
                          onClick={() => setDealBuilderQuantity(dealBuilderQuantity + 1)}
                          sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}
                          size={isMobile ? "small" : "medium"}
                        >
                          <AddCircleIcon />
                        </IconButton>
                      </Box>
                    </Box>

                    <Button
                      variant="contained"
                      size="large"
                      fullWidth
                      startIcon={<CheckCircleIcon />}
                      onClick={handleAddToDeal}
                      sx={{
                        bgcolor: '#ff6b35',
                        '&:hover': { bgcolor: '#e55a2b' },
                        borderRadius: 2,
                        fontWeight: 700,
                        py: isMobile ? 1 : 1.5,
                        fontSize: isMobile ? '0.8rem' : '1rem',
                      }}
                    >
                      Add to Deal / ڈیل میں شامل کریں
                    </Button>
                  </Paper>
                </Box>
              )}

              {/* Current Deal Items */}
              {dealForm.deal_items.length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#1a1a2e', mb: 1, fontSize: isMobile ? '0.8rem' : '0.9rem' }}>
                    Current Deal Items / موجودہ ڈیل آئٹمز:
                  </Typography>
                  <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: 'divider' }}>
                    <List dense>
                      {dealForm.deal_items.map((di, index) => (
                        <React.Fragment key={index}>
                          <ListItem
                            secondaryAction={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.5 : 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => updateDealItemQuantity(index, di.quantity - 1)}
                                  >
                                    <RemoveIcon fontSize="small" />
                                  </IconButton>
                                  <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 24, textAlign: 'center' }}>
                                    {di.quantity}
                                  </Typography>
                                  <IconButton 
                                    size="small" 
                                    onClick={() => updateDealItemQuantity(index, di.quantity + 1)}
                                  >
                                    <AddCircleIcon fontSize="small" />
                                  </IconButton>
                                </Box>
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2e7d32', minWidth: 80, textAlign: 'right' }}>
                                  {formatPrice(di.unit_price * di.quantity)}
                                </Typography>
                                <IconButton size="small" onClick={() => removeDealItem(index)} sx={{ color: '#f44336' }}>
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Box>
                            }
                          >
                            <ListItemAvatar>
                              <Avatar sx={{ bgcolor: '#ff6b3520', color: '#ff6b35', width: 36, height: 36 }}>
                                <RestaurantIcon fontSize="small" />
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText
                              primary={
                                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                                  {di.item_name} {di.variant_size && <span style={{ color: '#ff6b35' }}>({di.variant_size})</span>}
                                </Typography>
                              }
                              secondary={
                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.6rem' : '0.7rem' }}>
                                  {formatPrice(di.unit_price)} each
                                </Typography>
                              }
                            />
                          </ListItem>
                          {index < dealForm.deal_items.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                    <Box sx={{ p: 2, bgcolor: '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: isMobile ? '0.75rem' : '0.85rem' }}>
                        Original Total:
                      </Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e', fontSize: isMobile ? '0.85rem' : '1rem' }}>
                        {formatPrice(calculateDealOriginalTotal(dealForm.deal_items))}
                      </Typography>
                    </Box>
                  </Paper>
                </Box>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setDealDialog(false)} variant="outlined" sx={{ borderRadius: 2 }} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={saveDeal}
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!dealForm.name.trim() || !dealForm.price || dealForm.deal_items.length === 0}
            sx={{ bgcolor: '#ff6b35', '&:hover': { bgcolor: '#e55a2b' }, borderRadius: 2, fontWeight: 600 }}
            fullWidth={isMobile}
          >
            {dealDialogMode === 'add' ? 'Add Deal' : 'Update Deal'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== DELETE CONFIRMATION - RESPONSIVE ===== */}
      <Dialog 
        open={deleteConfirm.open} 
        onClose={() => setDeleteConfirm({ ...deleteConfirm, open: false })} 
        maxWidth="xs" 
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#f44336' }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete <strong>{deleteConfirm.name}</strong>?
            <br />
            <Typography component="span" variant="caption" sx={{ color: '#f44336' }}>
              This action cannot be undone.
            </Typography>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 1 : 0 }}>
          <Button onClick={() => setDeleteConfirm({ ...deleteConfirm, open: false })} variant="outlined" sx={{ borderRadius: 2 }} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (deleteConfirm.type === 'category') handleDeleteCategory();
              else if (deleteConfirm.type === 'item') handleDeleteItem();
              else if (deleteConfirm.type === 'deal') handleDeleteDeal();
            }}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            sx={{ borderRadius: 2, fontWeight: 600 }}
            fullWidth={isMobile}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* ===== SNACKBAR ===== */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ borderRadius: 2, fontWeight: 500 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MenuManagement;